import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import {
  RefreshCw, AlertTriangle, Search, ChevronDown, ChevronRight, Image as ImageIcon,
  Type, Sparkles, ShoppingBag, MessageSquare, Crop, Eye, Code2, Copy, ExternalLink,
  Layers, Video, History, ShieldAlert, Check,
} from 'lucide-react';

const API = 'https://api.glowytics.com/api';

/**
 * Meta Ad Inspector
 * ---------------------------------------------------------------
 * Everything Meta holds against an ad on one page: every text, every
 * media asset, the AI/Advantage+ enhancement flags, the promoted
 * object, and — separately — what Meta ACTUALLY delivered.
 *
 * The enhancement flags are the point. Meta never writes them to its
 * own change log, so an enhancement flipping on is invisible unless
 * you read it off the creative yourself.
 */

const IMPACT = {
  text: { label: 'Metni değiştirir', cls: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500', icon: Type },
  media: { label: 'Görseli/videoyu değiştirir', cls: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500', icon: ImageIcon },
  commerce: { label: 'Ürün/katalog ekler', cls: 'bg-violet-50 text-violet-700 border-violet-200', dot: 'bg-violet-500', icon: ShoppingBag },
  social: { label: 'Sosyal yüzey ekler', cls: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500', icon: MessageSquare },
  layout: { label: 'Yerleşim/kırpma', cls: 'bg-slate-50 text-slate-700 border-slate-200', dot: 'bg-slate-400', icon: Crop },
  other: { label: 'Diğer', cls: 'bg-gray-50 text-gray-600 border-gray-200', dot: 'bg-gray-400', icon: Sparkles },
};
const impactOf = (k) => IMPACT[k] || IMPACT.other;

const fmtMoney = (n) => (Number(n) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 }) + ' ₺';
const fmtNum = (n) => (Number(n) || 0).toLocaleString('tr-TR');
const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d) ? '—' : d.toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul', dateStyle: 'short', timeStyle: 'short' });
};

const statusTone = (s = '') => {
  const t = s.toUpperCase();
  if (t === 'ACTIVE') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (t.includes('ISSUE') || t.includes('DISAPPROVED') || t.includes('ERROR')) return 'bg-rose-50 text-rose-700 border-rose-200';
  if (t === 'PAUSED') return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-gray-50 text-gray-600 border-gray-200';
};

const Chip = ({ children, cls = 'bg-gray-50 text-gray-600 border-gray-200' }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-medium ${cls}`}>{children}</span>
);

const Section = ({ title, icon: Icon, count, children, defaultOpen = false, tone = 'text-gray-700' }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left"
      >
        {open ? <ChevronDown size={15} className="text-gray-400" /> : <ChevronRight size={15} className="text-gray-400" />}
        {Icon && <Icon size={15} className={tone} />}
        <span className={`text-sm font-semibold ${tone}`}>{title}</span>
        {count != null && <span className="text-xs text-gray-400">({count})</span>}
      </button>
      {open && <div className="px-3 pb-3 pt-1 border-t border-gray-100">{children}</div>}
    </div>
  );
};

const CopyBtn = ({ value }) => {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(typeof value === 'string' ? value : JSON.stringify(value, null, 2));
        setDone(true);
        setTimeout(() => setDone(false), 1200);
      }}
      className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-800"
    >
      {done ? <Check size={12} /> : <Copy size={12} />} {done ? 'kopyalandı' : 'kopyala'}
    </button>
  );
};

// --- media tile -------------------------------------------------------------
const MediaTile = ({ img, flagged }) => (
  <div className={`rounded-lg border overflow-hidden bg-white ${flagged ? 'border-rose-300 ring-1 ring-rose-200' : 'border-gray-200'}`}>
    <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden">
      {img.url ? (
        <img
          src={img.url}
          alt={img.libraryName || img.hash || ''}
          className="w-full h-full object-contain"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      ) : (
        <ImageIcon size={22} className="text-gray-300" />
      )}
    </div>
    <div className="p-2 space-y-1">
      <div className="text-[10px] font-mono text-gray-400 truncate" title={img.hash || ''}>{img.hash ? img.hash.slice(0, 16) : '—'}</div>
      {img.libraryName && <div className="text-[11px] text-gray-700 truncate" title={img.libraryName}>{img.libraryName}</div>}
      {img.width ? <div className="text-[10px] text-gray-400">{img.width}×{img.height}</div> : null}
      {img.spend != null && <div className="text-[11px] font-semibold text-gray-800">{fmtMoney(img.spend)}</div>}
      <div className="flex flex-wrap gap-1">
        {(img.sources || []).map((s) => <Chip key={s}>{s.replace('object_story_spec.', 'oss.')}</Chip>)}
        {flagged && <Chip cls="bg-rose-50 text-rose-700 border-rose-200">creative'de YOK</Chip>}
        {img.inLibrary === false && <Chip cls="bg-rose-100 text-rose-800 border-rose-300">kütüphanede YOK</Chip>}
      </div>
    </div>
  </div>
);

// --- single ad card ---------------------------------------------------------
const AdCard = ({ ad, brandId, accountId, open, onToggle }) => {
  const [raw, setRaw] = useState(false);
  const [previews, setPreviews] = useState(null);
  const [pvLoading, setPvLoading] = useState(false);

  const spend = ad.performance ? Number(ad.performance.spend) || 0 : 0;
  const onEnh = ad.enhancements.items.filter((e) => e.on);
  const risky = onEnh.filter((e) => ['text', 'media', 'commerce'].includes(e.impact));
  const undeclaredImgs = ad.delivered.images.filter((i) => !i.declared);
  const undeclaredTexts = ad.undeclared.filter((u) => u.type !== 'image');

  const loadPreview = async () => {
    if (previews) { setPreviews(null); return; }
    setPvLoading(true);
    try {
      const res = await fetch(
        `${API}/meta-ad-inspector/preview?brandId=${brandId}&adId=${ad.id}${accountId ? `&accountId=${accountId}` : ''}`,
        { credentials: 'include', headers: { 'X-Brand-ID': brandId } }
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setPreviews(j.previews || []);
    } catch (e) {
      toast.error('Önizleme alınamadı: ' + e.message);
    } finally {
      setPvLoading(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 text-left">
        {open ? <ChevronDown size={16} className="text-gray-400 mt-1" /> : <ChevronRight size={16} className="text-gray-400 mt-1" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 truncate">{ad.name}</span>
            <Chip cls={statusTone(ad.effective_status)}>{ad.effective_status}</Chip>
            {ad.isDynamicCreative && <Chip cls="bg-indigo-50 text-indigo-700 border-indigo-200">dinamik yaratıcı</Chip>}
          </div>
          <div className="mt-1 flex items-center gap-3 flex-wrap text-xs text-gray-500">
            <span className="font-mono">{ad.id}</span>
            <span>{ad.adsetName || '—'}</span>
            <span>güncellendi {fmtDate(ad.updated_time)}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-bold text-gray-900">{fmtMoney(spend)}</div>
          <div className="mt-1 flex gap-1 justify-end flex-wrap">
            {risky.length > 0 && (
              <Chip cls="bg-rose-50 text-rose-700 border-rose-200"><ShieldAlert size={11} />{risky.length} AI müdahalesi açık</Chip>
            )}
            {undeclaredImgs.length + undeclaredTexts.length > 0 && (
              <Chip cls="bg-amber-50 text-amber-700 border-amber-200">{undeclaredImgs.length + undeclaredTexts.length} tanımsız yayın</Chip>
            )}
          </div>
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2 border-t border-gray-100 pt-3 bg-gray-50/40">

          {/* AI enhancements — the headline */}
          <Section
            title="AI iyileştirmeleri (Advantage+)"
            icon={Sparkles}
            count={`${ad.enhancements.onCount} açık / ${ad.enhancements.total}`}
            defaultOpen
            tone={risky.length ? 'text-rose-700' : 'text-gray-700'}
          >
            {!ad.enhancements.hasSpec ? (
              <p className="text-sm text-gray-500 py-2">Bu creative'de <code>degrees_of_freedom_spec</code> yok — Meta bu reklam için iyileştirme ayarı tutmuyor.</p>
            ) : (
              <div className="space-y-1 mt-1">
                {ad.enhancements.items.map((e) => {
                  const im = impactOf(e.impact);
                  return (
                    <div
                      key={e.key}
                      className={`flex items-start gap-2 px-2 py-1.5 rounded ${e.on ? 'bg-white border border-gray-200' : 'opacity-45'}`}
                    >
                      <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${e.on ? im.dot : 'bg-gray-300'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-sm ${e.on ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{e.label}</span>
                          <span className="font-mono text-[10px] text-gray-400">{e.key}</span>
                          {e.on && <Chip cls={im.cls}>{im.label}</Chip>}
                        </div>
                        {e.on && <div className="text-xs text-gray-600 mt-0.5">{e.description}</div>}
                        {e.customizations.length > 0 && (
                          <div className="flex gap-1 flex-wrap mt-1">
                            {e.customizations.map((c) => (
                              <Chip key={c.key} cls={c.status === 'OPT_IN' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-gray-50 text-gray-500 border-gray-200'}>
                                {c.key}: {c.status}
                              </Chip>
                            ))}
                          </div>
                        )}
                      </div>
                      <Chip cls={e.on ? 'bg-rose-100 text-rose-800 border-rose-300' : 'bg-gray-100 text-gray-500 border-gray-200'}>
                        {e.status}
                      </Chip>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Texts */}
          <Section title="Metinler" icon={Type} count={
            ad.content.texts.bodies.length + ad.content.texts.titles.length +
            ad.content.texts.descriptions.length + ad.content.texts.ctas.length
          }>
            {['bodies', 'titles', 'descriptions', 'ctas', 'links'].map((k) => {
              const rows = ad.content.texts[k] || [];
              if (!rows.length) return null;
              const labels = { bodies: 'Birincil metin', titles: 'Başlık', descriptions: 'Açıklama', ctas: 'Buton (CTA)', links: 'Bağlantı' };
              return (
                <div key={k} className="mt-2">
                  <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-1">{labels[k]}</div>
                  <div className="space-y-1">
                    {rows.map((r, i) => (
                      <div key={i} className="bg-white border border-gray-200 rounded px-2 py-1.5">
                        <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">{r.text}</div>
                        <div className="flex gap-1 flex-wrap mt-1">
                          {r.sources.map((s) => <Chip key={s}>{s.replace('object_story_spec.', 'oss.')}</Chip>)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </Section>

          {/* Declared media */}
          <Section title="Creative'de tanımlı medya" icon={ImageIcon} count={ad.content.media.images.length + ad.content.media.videos.length}>
            {ad.content.media.images.length === 0 && ad.content.media.videos.length === 0 ? (
              <p className="text-sm text-gray-500 py-2">Creative'de doğrudan medya yok — muhtemelen katalog reklamı; görseller feed'den geliyor.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mt-2">
                  {ad.content.media.images.map((img, i) => <MediaTile key={i} img={img} />)}
                </div>
                {ad.content.media.videos.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {ad.content.media.videos.map((v) => (
                      <div key={v.id} className="flex items-center gap-2 bg-white border border-gray-200 rounded px-2 py-1.5">
                        {v.thumbnail
                          ? <img src={v.thumbnail} alt="" className="w-12 h-12 object-cover rounded" loading="lazy" />
                          : <Video size={16} className="text-gray-400" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-800 truncate">{v.title || 'video'}</div>
                          <div className="font-mono text-[10px] text-gray-400">{v.id}</div>
                        </div>
                        {v.permalink && (
                          <a href={v.permalink} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-gray-700"><ExternalLink size={13} /></a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            {ad.content.assetCustomizationRules > 0 && (
              <p className="text-xs text-gray-500 mt-2">
                {ad.content.assetCustomizationRules} adet yerleşim özelleştirme kuralı var — hangi görselin nerede çıkacağı kurala bağlı.
              </p>
            )}
          </Section>

          {/* Delivered — what actually ran */}
          <Section
            title="Gerçekte yayınlanan"
            icon={Eye}
            count={ad.delivered.images.length + ad.delivered.bodies.length + ad.delivered.titles.length}
            tone={undeclaredImgs.length + undeclaredTexts.length ? 'text-amber-700' : 'text-gray-700'}
          >
            <p className="text-xs text-gray-500 mt-1 mb-2">
              Meta'nın gösterim/harcama dağılımından geliyor. Creative'de tanımlı olmayanlar kırmızı.
              Katalog reklamlarında ürün görselleri doğal olarak burada çıkar — tek başına suç delili değil.
            </p>
            {ad.delivered.images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {ad.delivered.images.map((img, i) => <MediaTile key={i} img={img} flagged={!img.declared} />)}
              </div>
            )}
            {['bodies', 'titles', 'descriptions', 'ctas'].map((k) => {
              const rows = ad.delivered[k] || [];
              if (!rows.length) return null;
              const labels = { bodies: 'Yayınlanan birincil metin', titles: 'Yayınlanan başlık', descriptions: 'Yayınlanan açıklama', ctas: 'Yayınlanan buton' };
              return (
                <div key={k} className="mt-3">
                  <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-1">{labels[k]}</div>
                  <div className="space-y-1">
                    {rows.map((r, i) => (
                      <div key={i} className={`rounded px-2 py-1.5 border ${r.declared === false ? 'bg-rose-50 border-rose-300' : 'bg-white border-gray-200'}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-sm text-gray-800 whitespace-pre-wrap break-words flex-1">{r.text || '—'}</div>
                          <div className="text-right shrink-0">
                            <div className="text-xs font-semibold text-gray-800">{fmtMoney(r.spend)}</div>
                            <div className="text-[10px] text-gray-400">{fmtNum(r.impressions)} gösterim</div>
                          </div>
                        </div>
                        {r.declared === false && (
                          <Chip cls="bg-rose-100 text-rose-800 border-rose-300">creative'de bu metin YOK</Chip>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {ad.delivered.images.length === 0 && ad.delivered.bodies.length === 0 && (
              <p className="text-sm text-gray-500 py-2">Seçili dönemde bu reklam için varlık dağılımı dönmedi (gösterim yok ya da format desteklemiyor).</p>
            )}
          </Section>

          {/* Targeting / promoted object */}
          <Section title="Promosyon & hedef nesne" icon={ShoppingBag}>
            <div className="mt-2 space-y-2 text-sm">
              <div className="bg-white border border-gray-200 rounded p-2">
                <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-1">promoted_object</div>
                <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all">{JSON.stringify(ad.promoted_object, null, 2) || '—'}</pre>
              </div>
              {ad.creative && ad.creative.product_set_id && (
                <div className="bg-white border border-gray-200 rounded p-2">
                  <span className="text-gray-500 text-xs">creative.product_set_id: </span>
                  <span className="font-mono text-xs">{ad.creative.product_set_id}</span>
                </div>
              )}
              {ad.tracking_specs && (
                <div className="bg-white border border-gray-200 rounded p-2">
                  <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-1">tracking_specs</div>
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all">{JSON.stringify(ad.tracking_specs, null, 2)}</pre>
                </div>
              )}
              {ad.issues_info && (
                <div className="bg-rose-50 border border-rose-200 rounded p-2">
                  <div className="text-[11px] uppercase tracking-wide text-rose-500 font-semibold mb-1">issues_info</div>
                  <pre className="text-xs text-rose-800 whitespace-pre-wrap break-all">{JSON.stringify(ad.issues_info, null, 2)}</pre>
                </div>
              )}
            </div>
          </Section>

          {/* Change log for this ad */}
          <Section title="Bu reklamın değişiklik günlüğü (90g)" icon={History} count={ad.activities.length}>
            {ad.activities.length === 0 ? (
              <p className="text-sm text-gray-500 py-2">Kayıt yok.</p>
            ) : (
              <div className="space-y-1 mt-2">
                {ad.activities.map((a, i) => (
                  <div key={i} className="flex items-start gap-2 bg-white border border-gray-200 rounded px-2 py-1.5">
                    <Chip cls={a.actor_name === 'Meta' ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-gray-50 text-gray-600 border-gray-200'}>
                      {a.actor_name || '—'}
                    </Chip>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-800">{a.translated_event_type || a.event_type}</div>
                      <div className="text-[11px] text-gray-400">{fmtDate(a.event_time)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Rendered preview */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={loadPreview}
              disabled={pvLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm hover:bg-gray-50 disabled:opacity-50"
            >
              <Eye size={14} /> {pvLoading ? 'yükleniyor…' : previews ? 'önizlemeyi kapat' : 'Gerçek görünümü aç'}
            </button>
            <button
              onClick={() => setRaw((r) => !r)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm hover:bg-gray-50"
            >
              <Code2 size={14} /> ham JSON
            </button>
            {ad.preview_shareable_link && (
              <a href={ad.preview_shareable_link} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm hover:bg-gray-50">
                <ExternalLink size={14} /> Meta önizleme
              </a>
            )}
          </div>

          {previews && (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {previews.map((p) => (
                <div key={p.format} className="shrink-0">
                  <div className="text-[11px] text-gray-500 mb-1">{p.format}</div>
                  {p.error
                    ? <div className="w-80 h-40 flex items-center justify-center text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded">{p.error}</div>
                    : <div className="border border-gray-200 rounded overflow-hidden bg-white" dangerouslySetInnerHTML={{ __html: p.body }} />}
                </div>
              ))}
            </div>
          )}

          {raw && (
            <div className="bg-gray-900 rounded-lg p-3 overflow-auto max-h-[500px]">
              <div className="flex justify-end mb-1"><CopyBtn value={ad.creative} /></div>
              <pre className="text-[11px] text-emerald-300 whitespace-pre-wrap break-all">{JSON.stringify(ad.creative, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- page -------------------------------------------------------------------
export default function MetaAdInspector() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || localStorage.getItem('currentBrandId') || '';

  const [campaigns, setCampaigns] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dump, setDump] = useState(null);
  const [loadingC, setLoadingC] = useState(false);
  const [loadingD, setLoadingD] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [days, setDays] = useState(30);
  const [openAds, setOpenAds] = useState({});
  const [onlyRisky, setOnlyRisky] = useState(false);

  const headers = useMemo(() => ({ 'X-Brand-ID': brandId }), [brandId]);

  useEffect(() => {
    if (!brandId) return;
    fetch(`${API}/meta-ad-inspector/accounts?brandId=${brandId}`, { credentials: 'include', headers })
      .then((r) => r.json())
      .then((j) => setAccounts(j.accounts || []))
      .catch(() => {});
  }, [brandId, headers]);

  const loadCampaigns = useCallback(async () => {
    if (!brandId) return;
    setLoadingC(true); setError(null);
    try {
      const res = await fetch(
        `${API}/meta-ad-inspector/campaigns?brandId=${brandId}&days=${days}${accountId ? `&accountId=${accountId}` : ''}`,
        { credentials: 'include', headers }
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setCampaigns(j.campaigns || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingC(false);
    }
  }, [brandId, days, accountId, headers]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const loadDump = useCallback(async (campaignId) => {
    setLoadingD(true); setError(null); setDump(null); setOpenAds({});
    try {
      const res = await fetch(
        `${API}/meta-ad-inspector/dump?brandId=${brandId}&campaignId=${campaignId}&days=${days}${accountId ? `&accountId=${accountId}` : ''}`,
        { credentials: 'include', headers }
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setDump(j);
      (j.warnings || []).forEach((w) => toast(w, { icon: '⚠️' }));
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    } finally {
      setLoadingD(false);
    }
  }, [brandId, days, accountId, headers]);

  const filteredCampaigns = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? campaigns.filter((c) => c.name.toLowerCase().includes(t) || c.id.includes(t)) : campaigns;
  }, [campaigns, q]);

  const visibleAds = useMemo(() => {
    if (!dump) return [];
    return onlyRisky
      ? dump.ads.filter((a) => a.enhancements.items.some((e) => e.on && ['text', 'media', 'commerce'].includes(e.impact)))
      : dump.ads;
  }, [dump, onlyRisky]);

  if (!brandId) {
    return <div className="p-8 text-gray-500">Önce bir marka seçin.</div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
      {/* header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Layers size={22} className="text-blue-600" /> Meta Reklam Röntgeni
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Bir reklama bağlı her şey: metinler, medya, AI iyileştirmeleri, promosyon nesnesi ve gerçekte yayınlanan içerik.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {accounts.length > 1 && (
            <select value={accountId} onChange={(e) => { setAccountId(e.target.value); setDump(null); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white">
              <option value="">Varsayılan hesap</option>
              {accounts.map((a) => <option key={a.accountId} value={a.accountId}>{a.name}</option>)}
            </select>
          )}
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white">
            <option value={7}>Son 7 gün</option>
            <option value={30}>Son 30 gün</option>
            <option value={90}>Son 90 gün</option>
          </select>
          <button onClick={loadCampaigns} disabled={loadingC}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw size={14} className={loadingC ? 'animate-spin' : ''} /> Yenile
          </button>
        </div>
      </div>

      {/* the standing warning — this is why the page exists */}
      <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
        <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
        <p className="text-sm text-amber-900">
          Meta, AI iyileştirmelerini <strong>kendi değişiklik günlüğüne yazmıyor</strong>. Bir ayar açıldığında hiçbir bildirim
          düşmez; ancak burada creative'in üzerinden okunabilir. Kırmızı işaretli her satır, Meta'nın reklamına
          müdahale etme <em>iznidir</em>.
        </p>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-800">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">
        {/* campaign list */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden self-start">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kampanya ara…"
                className="w-full pl-8 pr-2 py-1.5 border border-gray-200 rounded-lg text-sm" />
            </div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-gray-100">
            {loadingC && <div className="p-4 text-sm text-gray-500">Kampanyalar yükleniyor…</div>}
            {!loadingC && filteredCampaigns.length === 0 && <div className="p-4 text-sm text-gray-500">Kampanya yok.</div>}
            {filteredCampaigns.map((c) => (
              <button key={c.id} onClick={() => { setSelected(c); loadDump(c.id); }}
                className={`w-full text-left px-3 py-2 hover:bg-blue-50 ${selected?.id === c.id ? 'bg-blue-50 border-l-2 border-blue-600' : ''}`}>
                <div className="text-sm font-medium text-gray-900 truncate">{c.name}</div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <Chip cls={statusTone(c.effective_status)}>{c.effective_status}</Chip>
                  <span className="text-xs text-gray-500">{fmtMoney(c.spend)}</span>
                  <span className="text-[11px] text-gray-400">{c.objective}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* detail */}
        <div className="space-y-4">
          {loadingD && (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500">
              <RefreshCw size={20} className="animate-spin mx-auto mb-2" />
              Reklamlar, creative'ler, iyileştirme ayarları ve yayın dağılımı çekiliyor…
            </div>
          )}

          {!loadingD && !dump && (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500">
              Soldan bir kampanya seçin.
            </div>
          )}

          {!loadingD && dump && (
            <>
              {/* rollup */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h2 className="font-bold text-gray-900">{dump.campaign?.name || selected?.name}</h2>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {dump.accountName} · act_{dump.accountId} · {dump.ads.length} reklam · {dump.adsets.length} reklam seti
                      {dump.catalogs.length > 0 && ` · ${dump.catalogs.length} katalog`}
                      {dump.productSets.length > 0 && ` · ${dump.productSets.length} ürün seti`}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={onlyRisky} onChange={(e) => setOnlyRisky(e.target.checked)} />
                    Sadece AI müdahalesi açık olanlar
                  </label>
                </div>

                {dump.enhancementRollup.length > 0 ? (
                  <div className="mt-3">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-2">
                      Bu kampanyada AÇIK olan iyileştirmeler
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {dump.enhancementRollup.map((r) => {
                        const im = impactOf(r.impact);
                        return (
                          <span key={r.key} title={r.description}
                            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs ${im.cls}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${im.dot}`} />
                            {r.label}
                            <span className="opacity-60">· {r.ads} reklam</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-emerald-700 mt-3">Bu kampanyada açık AI iyileştirmesi yok.</p>
                )}

                {/* product sets */}
                {dump.productSets.length > 0 && (
                  <div className="mt-4">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Ürün setleri</div>
                    <div className="space-y-1">
                      {dump.productSets.map((ps) => (
                        <div key={ps.id} className="bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-gray-900">{ps.name || ps.id}</span>
                            {ps.product_count != null && <Chip>{fmtNum(ps.product_count)} ürün</Chip>}
                            {ps.product_catalog && <Chip>katalog: {ps.product_catalog.name}</Chip>}
                            {ps.__unreadable && <Chip cls="bg-rose-50 text-rose-700 border-rose-200">okunamadı</Chip>}
                          </div>
                          {ps.filter && (
                            <pre className="text-[11px] text-gray-600 mt-1 whitespace-pre-wrap break-all">{JSON.stringify(ps.filter)}</pre>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* ads */}
              <div className="space-y-2">
                {visibleAds.map((ad) => (
                  <AdCard
                    key={ad.id}
                    ad={ad}
                    brandId={brandId}
                    accountId={accountId}
                    open={!!openAds[ad.id]}
                    onToggle={() => setOpenAds((s) => ({ ...s, [ad.id]: !s[ad.id] }))}
                  />
                ))}
                {visibleAds.length === 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-gray-500">
                    Filtreye uyan reklam yok.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
